import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import http from "../services/http-common";
import NewsModel from "../models/news";
import ActivitiesModel from "../models/activities";
import ArticleModel from "../models/article";
import ServiceModel from "../models/service";
import InfographicService from "../services/infographic";
import timeFunction from "../functions/getTime";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendar } from "@fortawesome/free-solid-svg-icons";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Hamburger from "../components/Hamburger";
import TemplateService from "../services/template";
import RichText from "../components/content/RichText";
import BulletParagraph from "../components/content/BulletParagraph";
import ContentImage from "../components/content/ContentImage";

function useQuery() {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
}

export default function Preview() {
  const query = useQuery();
  const uid = query.get("uid");
  const documentId = query.get("documentId");
  const locale = query.get("locale") || "th";
  const status = query.get("status") || "draft";

  const [contentData, setContentData] = useState(null);
  const [pageType, setPageType] = useState("");
  const [templateData, setTemplateData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    getTemplateData();
    if (uid && documentId) {
      fetchPreviewData();
    } else {
      setError(true);
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [uid, documentId]);

  const getTemplateData = async () => {
    try {
      let res = await TemplateService.getTemplate();
      setTemplateData(res);
    } catch {
      console.error("Failed to load template");
    }
  };

  const fetchPreviewData = async () => {
    try {
      setLoading(true);
      setError(false);

      let endpoint = "";
      let model = null;
      let page = "";

      if (uid === "api::new.new") {
        endpoint = "news";
        model = NewsModel;
        page = "news";
      } else if (uid === "api::activity.activity") {
        endpoint = "activities";
        model = ActivitiesModel;
        page = "activities";
      } else if (uid === "api::article.article") {
        endpoint = "articles";
        model = ArticleModel;
        page = "article";
      } else if (uid === "api::infographic.infographic") {
        // หน้ารายละเอียดของอินโฟกราฟิกแสดงภาพเต็ม ใช้หน้าจริงพร้อม ?status= แทน
        const slug = await InfographicService.getSlugByDocumentId(documentId, status);
        if (slug) {
          window.location.replace(
            `/${locale || "th"}/infographic/${encodeURIComponent(slug)}?status=${status}`
          );
        } else {
          setError(true);
          setLoading(false);
        }
        return;
      } else if (uid === "api::service.service") {
        endpoint = "services";
        model = ServiceModel;
        page = "service";
      } else {
        // Fallback or unknown
        setError(true);
        setLoading(false);
        return;
      }

      setPageType(page);

      // Fetch from Strapi using documentId and status
      const statusQuery = page === "activities" ? "" : `&status=${status}`;
      const res = await http.get(
        `/${endpoint}?filters[documentId][$eq]=${documentId}${statusQuery}&pLevel=5`,
      );

      if (res.data && res.data.data && res.data.data.length > 0) {
        const rawData = res.data.data[0];
        const formattedData = await model.get(rawData);
        setContentData(formattedData);
      } else {
        setError(true);
      }
    } catch (err) {
      console.error("Preview fetch error:", err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-20 text-center text-xl">
        กำลังโหลดตัวอย่าง (Loading Preview)...
      </div>
    );
  }

  if (error || !contentData) {
    return (
      <div className="p-20 text-center text-xl text-red-500">
        ไม่พบข้อมูล หรือเกิดข้อผิดพลาดในการโหลด Preview
      </div>
    );
  }

  // ── Service-specific rendering ──────────────────────────────────────
  if (pageType === "service") {
    return (
      <div className="App">
        <Navbar data={templateData} />
        <Hamburger data={templateData} />

        <div className="service-info pt-[135px] lg:pt-[0px]">
          <div className="section">
            <div className="page-container">
              <div className="mt-[92px] mb-[300px]">
                <div className="bg-yellow-200 text-yellow-800 p-3 rounded mb-5 font-bold text-center">
                  ⚠️ นี่คือหน้าจอ Preview (สถานะ: {status})
                </div>

                {contentData.link && contentData.link.label_TH && (
                  <h1 className="text-heading font-bold text-slateGray text-left">
                    {locale === "th"
                      ? contentData.link.label_TH
                      : contentData.link.label_EN}
                  </h1>
                )}

                <div>
                  {contentData.content &&
                    contentData.content.map((service, i) => (
                      <div key={i}>
                        {service.__component === "global.title" && (
                          <h1 className="text-heading font-bold text-slateGray text-left my-[50px]">
                            {locale === "th"
                              ? service.title_th
                              : service.title_en}
                          </h1>
                        )}

                        {service.__component === "global.image-content" && (
                          <div
                            className={
                              `flex items-start gap-[46px] ` +
                              (!service.imageIsLeft && "flex-row-reverse")
                            }
                          >
                            <ContentImage image={service?.image} lang={locale} />
                            <RichText
                              className="w-full"
                              html={
                                locale === "th"
                                  ? service.detail?.[0]?.detail_TH
                                  : service.detail?.[0]?.detail_EN
                              }
                            />
                          </div>
                        )}

                        {(service.__component === "global.detail" ||
                          service.__component === "global.rich-detail") && (
                          <RichText
                            html={
                              locale === "th"
                                ? service.detail_TH
                                : service.detail_EN
                            }
                          />
                        )}

                        {service.__component === "about.bullet" && (
                          <div className="mt-[32px]">
                            {BulletParagraph(
                              locale === "th"
                                ? service.title_TH
                                : service.title_EN,
                              locale === "th"
                                ? service.detail_TH
                                : service.detail_EN,
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <Footer data={templateData} />
      </div>
    );
  }

  // ── News / Activities / Article rendering ───────────────────────────
  // Same title logic as ContentDetail
  const titleTH =
    pageType === "activities"
      ? contentData?.title?.event_TH
      : contentData?.title?.detail_TH;
  const titleEN =
    pageType === "activities"
      ? contentData?.title?.event_EN
      : contentData?.title?.detail_EN;
  const title = locale === "th" ? titleTH : titleEN;

  // Same HTML body logic as ContentDetail
  let htmlBody = "";
  if (
    pageType === "activities" &&
    contentData.body &&
    contentData.body.length > 0
  ) {
    htmlBody =
      locale === "th"
        ? contentData.body[0]?.body_TH
        : contentData.body[0]?.body_EN;
  } else if (contentData.content && contentData.content.length > 0) {
    htmlBody =
      locale === "th"
        ? contentData.content[0]?.body_TH
        : contentData.content[0]?.body_EN;
  }

  return (
    <div className="App">
      <Navbar data={templateData} />
      <Hamburger data={templateData} />

      <div className="content-detail pt-[135px] lg:pt-[0px]">
        <div className="section">
          <div className="page-container">
            <div className="mt-[92px] mb-[46px]">
              {pageType !== "activities" && (
                <div className="bg-yellow-200 text-yellow-800 p-3 rounded mb-5 font-bold text-center">
                  ⚠️ นี่คือหน้าจอ Preview (สถานะ: {status})
                </div>
              )}

              <h1 className="text-heading font-bold text-slateGray text-left capitalize mt-[25px]">
                {title}
              </h1>

              <div className="w-[120px] border-[1px] border-black my-[22px]" />

              <div className="flex justify-start items-start gap-[18px]">
                <div className="flex items-center gap-[12px]">
                  <FontAwesomeIcon
                    icon={faCalendar}
                    className="text-lead cursor-pointer"
                  />
                  <p className="text-body mt-[3px]">
                    {timeFunction.getTime(
                      pageType === "activities"
                        ? contentData.date?.startDate
                        : contentData.publishedAt,
                      locale,
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-[12px]">
                  <p className="text-body mt-[3px]">
                    {contentData?.tag?.map((item, i) => (
                      <span key={i}>
                        <span className="text-blue">#{item.tag}</span>
                        {i + 1 < contentData?.tag?.length && ", "}
                      </span>
                    ))}
                  </p>
                </div>
              </div>

              {htmlBody && (
                <RichText
                  className="my-[30px]"
                  html={htmlBody}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer data={templateData} />
    </div>
  );
}

